import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChildren,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDrawerMode } from '@angular/material/sidenav';
import { ApplicationService } from '@klickdata/core/application';
import {
    MobileService,
    SideNaveActionsTypes,
    SideNaveData,
    SideNaveDataTypes,
    SideNaveResponseData,
} from '@klickdata/core/mobile/src/mobile.service';
import { EMPTY, Observable, Subject, concat, of, timer } from 'rxjs';
import { first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MenuSideMobileBottomSheetComponent } from '../menu-side-mobile-bottom-sheet/menu-side-mobile-bottom-sheet.component';
import { User } from '@klickdata/core/user';
import { AuthService } from '@klickdata/core/auth';
import { GloalSearchService } from '@klickdata/core/global-search';
export enum Direction {
    Up = 'Up',
    Down = 'Down',
}
@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent implements OnInit {
    public isMobile: Observable<boolean>;
    public showingMenu: Observable<boolean>;
    public sideNavData$: Observable<SideNaveData>;
    public version$: Observable<{ version: string; date: string }>;
    @ContentChild(TemplateRef)
    public template: TemplateRef<void>;
    public position: 'start' | 'end' = 'end';
    public mode: MatDrawerMode = 'over';
    public isSideNavDataMenu$: Subject<boolean> = new Subject();
    SideNaveDataTypes = SideNaveDataTypes;
    SideNaveActionsTypes = SideNaveActionsTypes;
    @ViewChildren('menuSideComp') menuSideComps: QueryList<any>;
    private isMob: boolean;
    public userIsAU$: Observable<boolean>;
    public isSearchBoxActive: Observable<boolean>;
    private clearDrawerTimer: ReturnType<typeof setTimeout>;

    constructor(
        protected mobile: MobileService,
        protected changeRef: ChangeDetectorRef,
        protected appService: ApplicationService,
        protected cdr: ChangeDetectorRef,
        protected bottomSheet: MatBottomSheet,
        protected auth: AuthService,
        public searchService: GloalSearchService
    ) {
        this.version$ = appService.getVersion();
    }

    public ngOnInit() {
        this.isSearchBoxActive = this.searchService.getSearchBoxState();
        this.userIsAU$ = this.auth.getUser().pipe(
            first(),
            map((user) => !user.isAdmin())
        );
        this.isMobile = this.mobile.isMobile().pipe(
            tap((ismob) => (this.isMob = ismob)),
            map((res) => {
                this.update();
                return res;
            })
        );
        this.showingMenu = this.mobile.showingMenu().pipe(
            map((res) => {
                this.update();
                return res;
            })
        );
        this.sideNavData$ = this.mobile.getSideNavSub().pipe(
            switchMap((navData) => this.closePendingPanel(navData)),
            switchMap((navData) => {
                return this.mobile
                    .isMobile()
                    .pipe(
                        switchMap((isMob) =>
                            isMob && navData.dataType !== SideNaveDataTypes.MOBILE_MENU
                                ? this.showBottomSheet(navData)
                                : of(navData)
                        )
                    );
            }),
            map((res) => {
                this.isSideNavDataMenu$.next(res.dataType !== SideNaveDataTypes.MOBILE_MENU);
                this.update();
                return res;
            })
        );
    }
    public showBottomSheet(navData: SideNaveData) {
        this.bottomSheet
            .open(MenuSideMobileBottomSheetComponent, {
                data: navData,
                panelClass: 'sheet-wrapper',
            })
            .afterDismissed()
            .subscribe((results) => {
                this.onClose(results);
            });

        return EMPTY;
    }
    public onClose(responsData?: SideNaveResponseData): void {
        this.mobile.showMenu(false);
        this.update();
        if (!responsData) {
            this.clearDrawer();
        }
        if (responsData?.action) {
            this.mobile.updateSideNavAction(responsData.action);
        }
        if (responsData?.data) {
            this.mobile.setSideNavResponseData(responsData.data);
        }
    }
    public clearDrawer() {
        this.cancelClearDrawer();
        this.clearDrawerTimer = setTimeout(() => {
            this.clearDrawerTimer = null;
            this.mobile.showMenu(false);
            this.notifyPanelClosed();
            this.mobile.updateSideNavSub(
                this.isMob
                    ? { dataType: SideNaveDataTypes.MOBILE_MENU, ignoreDrawer: true }
                    : { dataType: SideNaveDataTypes.CLEARDRAWER, ignoreDrawer: true }
            );
        }, 500);
    }
    // A new panel opened while the previous one was still closing (#4524):
    // close the old one now, but keep the drawer open for the new one.
    private closePendingPanel(navData: SideNaveData): Observable<SideNaveData> {
        if (navData.ignoreDrawer || !this.stopPendingClear()) {
            return of(navData);
        }
        if (this.isMob) {
            return of(navData);
        }
        // Render the empty drawer first, so a panel of the same type is rebuilt instead of reused.
        return concat(
            of({ dataType: SideNaveDataTypes.CLEARDRAWER, ignoreDrawer: true }),
            timer(0).pipe(map(() => navData))
        );
    }
    // The drawer was reopened (e.g. by the mobile menu button) before the wipe ran.
    public onDrawerOpening() {
        this.stopPendingClear();
    }
    private stopPendingClear(): boolean {
        if (!this.clearDrawerTimer) {
            return false;
        }
        this.cancelClearDrawer();
        this.notifyPanelClosed();
        return true;
    }
    private notifyPanelClosed() {
        this.mobile.updateSideNavAction(SideNaveActionsTypes.PANEL_CLOSED);
        this.mobile.setSideNavResponseData({ data: null });
    }
    private cancelClearDrawer() {
        clearTimeout(this.clearDrawerTimer);
        this.clearDrawerTimer = null;
    }
    protected update() {
        this.changeRef.markForCheck();
    }
}
